/**
 * Siebter Block der Uebersicht (V-15): Zeitverteilung fuer heute - kleiner
 * Ring mit der unverplanten Zeit in der Mitte, die drei groessten Bereiche als Text.
 * Nur anzeigen (V-02); Heute/Woche ist reine Navigation. "Oeffnen" fuehrt zur
 * ausfuehrlichen Ansicht (Tagesplan bzw. Wochenplanung).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PieChart } from 'lucide-react';

import { Card } from '@/modules/dashboard/Card';
import { useAreaMap } from '@/modules/areas/useAreas';
import { useZeitverteilung } from '@/modules/zeit/useZeitverteilung';
import { ZeitRing } from '@/modules/zeit/ZeitRing';
import { ringSegmente, zeilen } from '@/modules/zeit/segmente';
import { stunden } from '@/domain/zeit/verteilung';
import { zeitraumTage } from '@/domain/zeit/zeitraum';
import { isoWeekKey } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import type { LocalDate } from '@/domain/types';

export function ZeitBlock({ heute }: { heute: LocalDate }) {
  const [art, setArt] = useState<'tag' | 'woche'>('tag');
  const tage = zeitraumTage(art, heute);
  const v = useZeitverteilung(tage, 'tatsaechlich');
  const liste = zeilen(v, useAreaMap());
  const top = liste.filter((z) => z.art === 'bereich' && z.minuten > 0).slice(0, 3);

  return (
    <Card title="Zeitverteilung" icon={PieChart} to={art === 'tag' ? '/aufgaben' : `/rueckblick?wochenplanung=${isoWeekKey(heute)}`}>
      <div className="flex flex-col gap-2.5">
        <div role="group" aria-label="Zeitraum der Zeitverteilung" className="flex gap-1">
          {(['tag', 'woche'] as const).map((a) => (
            <button key={a} type="button" aria-pressed={art === a} onClick={() => setArt(a)}
              className={cn('rounded-full border px-2.5 py-0.5 text-[12px]', art === a ? 'border-accent bg-accent/10 text-ink' : 'border-line text-ink-muted hover:text-ink')}>
              {a === 'tag' ? 'Heute' : 'Woche'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <ZeitRing segmente={ringSegmente(liste)} groesse={112} mitte={stunden(v.unverplant)} unter="unverplant"
            label={`Zeitverteilung ${art === 'tag' ? 'heute' : 'diese Woche'}: ${liste.map((z) => `${z.name} ${z.text}`).join(', ')}`} />
          <ul className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm">
            {top.length === 0 && <li className="text-ink-faint">Nichts verplant.</li>}
            {top.map((z) => (
              <li key={z.key} className="flex items-center gap-1.5">
                <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: z.farbe }} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{z.name}</span>
                <span className="tabular-nums text-ink-muted">{z.text}</span>
              </li>
            ))}
            <li className="flex items-center gap-1.5 text-ink-muted">
              <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: liste.find((z) => z.key === 'schlaf')!.farbe }} aria-hidden="true" />
              <span className="flex-1 truncate">{liste.find((z) => z.key === 'schlaf')!.name}</span>
              <span className="tabular-nums">{stunden(v.schlaf)}</span>
            </li>
          </ul>
        </div>
        {v.ueberplant > 0 && <p className="text-[12px] text-danger">{art === 'tag' ? `Tag ist um ${stunden(v.ueberplant)} überplant.` : `${v.ueberplanteTage.length} Tage überplant.`}</p>}
        {v.unbestaetigt > 0 && (
          <Link to={`/journal?datum=${heute}&typ=abend`} className="text-[12px] text-ink-muted hover:text-ink hover:underline">
            {v.unbestaetigt === 1 ? '1 Termin' : `${v.unbestaetigt} Termine`} noch nicht bestätigt – zählen vorläufig wie geplant
          </Link>
        )}
      </div>
    </Card>
  );
}
